import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import { scrollToId } from "@/lib/scroll";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-10 h-px bg-terra" />
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-moss" data-testid="hero-eyebrow">
              Learning Lights Initiative
            </span>
          </motion.div>

          <h1 className="font-serif font-bold tracking-tighter text-forest leading-[0.95] text-5xl sm:text-7xl lg:text-[6.5rem]" data-testid="hero-heading">
            <MaskedLine delay={0.2}>Every child</MaskedLine>
            <MaskedLine delay={0.35}>
              deserves a
            </MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic text-terra">brighter</span> tomorrow.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-moss"
            data-testid="hero-subcopy"
          >
            A Hyderabad-based education NGO transforming government schools across
            Telangana — deploying teachers where they're needed most, building
            activity-based classrooms, and lighting up learning one school at a time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              onClick={() => scrollToId("get-involved")}
              data-testid="hero-donate-btn"
              className="group bg-terra text-bone font-bold px-8 py-4 flex items-center gap-3 hover:-translate-y-1 hover:shadow-xl transition-all duration-200"
            >
              Light a Lamp — Donate
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("get-involved")}
              data-testid="hero-volunteer-btn"
              className="border border-forest/40 text-forest font-bold px-8 py-4 hover:bg-forest hover:text-bone hover:-translate-y-1 transition-all duration-200"
            >
              Become a Volunteer
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-0 translate-x-5 translate-y-5 bg-terra/90 -z-0" />
            <div className="relative overflow-hidden rounded-t-[999px] border border-forest/20" data-testid="hero-image-frame">
              <motion.img
                style={{ y: imgY, scale: 1.18 }}
                src="/photos/hero-classroom.webp"
                alt="Students holding their new workbooks in front of the classroom blackboard"
                className="w-full h-[420px] lg:h-[560px] object-cover img-warm"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="absolute -bottom-6 -left-6 lg:-left-12 bg-forest text-bone px-6 py-5 shadow-xl"
              data-testid="hero-stat-chip"
            >
              <div className="flex items-center gap-2 text-terra">
                <Sparkle size={16} weight="fill" />
                <span className="text-[10px] font-bold tracking-[0.25em] uppercase">Since 2024</span>
              </div>
              <p className="font-serif text-2xl mt-1">2500+ children reached</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-moss">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-px h-10 bg-forest/40 block"
        />
      </motion.div>
    </section>
  );
};
